<template>
  <v-card :elevation="$vuetify.breakpoint.xsOnly ? 0 : ''" :class="$vuetify.breakpoint.xsOnly ? 'mb-2' : 'mb-4'">
    <!-- Mobiili: chip + ensisijainen toiminto leveänä nappina, muut valikossa -->
    <v-card-text v-if="$vuetify.breakpoint.xsOnly" class="py-2">
      <div class="d-flex align-center mb-2">
        <quotation-status-chip :status="quotation.status" />
        <span v-if="sentText" class="caption ml-2">{{ sentText }}</span>
        <span v-if="quotation.version > 1" class="caption grey--text ml-2">{{ $t('quotations.version') }} {{ quotation.version }}</span>
        <v-spacer />
        <v-menu v-if="secondary.length > 0" offset-y left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" :disabled="disabled" :title="$t('quotations.actions.change_status')">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item v-for="a in secondary" :key="a.status" :disabled="a.disabled" @click="$emit('change-status', a.status)">
              <v-list-item-title>{{ a.label }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <v-btn v-if="primary.status" block color="primary" :disabled="disabled || primary.disabled" @click="$emit('change-status', primary.status)">
        {{ primary.label }}
      </v-btn>
    </v-card-text>

    <!-- Desktop: vaihepolku + ensisijainen nappi ja muut toiminnot valikossa -->
    <v-card-text v-else class="d-flex align-center flex-wrap py-1 pr-4">
      <v-stepper :value="step" alt-labels flat class="flex-grow-1 elevation-0 stepper-plain">
        <v-stepper-header class="elevation-0">
          <template v-for="(s, i) in steps">
            <v-stepper-step :key="s.key" :step="i + 1" :complete="i + 1 < step || isFinal" :color="s.color" :complete-icon="s.icon">
              {{ s.label }}
              <small v-if="s.key === 'READY' && sentText">{{ sentText }}</small>
            </v-stepper-step>
            <v-divider v-if="i < steps.length - 1" :key="`d${s.key}`" />
          </template>
        </v-stepper-header>
      </v-stepper>
      <div v-if="actions.length > 0" class="d-flex align-center flex-shrink-0 ml-auto pl-4">
        <v-btn color="primary" :disabled="disabled || primary.disabled" @click="$emit('change-status', primary.status)">
          {{ primary.label }}
        </v-btn>
        <v-menu v-if="secondary.length > 0" offset-y left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" :disabled="disabled" :title="$t('quotations.actions.change_status')" class="ml-1">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item v-for="a in secondary" :key="a.status" :disabled="a.disabled" @click="$emit('change-status', a.status)">
              <v-list-item-title>{{ a.label }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </v-card-text>

    <v-card-text v-if="blockers.length > 0 || warnings.length > 0" class="pt-0">
      <v-alert v-if="blockers.length > 0" type="warning" text dense class="mb-2">
        <div class="font-weight-medium">{{ $t('quotations.blockers.title') }}</div>
        <ul class="pl-4">
          <li v-for="b in blockers" :key="b">{{ $t(`quotations.blockers.${b}`) }}</li>
        </ul>
      </v-alert>
      <v-alert v-if="warnings.length > 0" type="info" text dense class="mb-0">
        <span v-for="w in warnings" :key="w">{{ $t(`quotations.blockers.${w}`) }}</span>
      </v-alert>
    </v-card-text>
  </v-card>
</template>

<script>
import QuotationStatusChip from './QuotationStatusChip';
import { FINAL_STATUSES as FINAL } from '@/helpers/quotationStatus';

export default {
  name: 'QuotationStatusStepper',
  components: { QuotationStatusChip },
  props: {
    quotation: { type: Object, required: true },
    blockers: { type: Array, default: () => [] },
    warnings: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
  },
  computed: {
    isFinal() {
      return FINAL.includes(this.quotation.status);
    },
    step() {
      return { REQUEST: 1, CALCULATION: 2, REVIEW: 3, READY: 4 }[this.quotation.status] || 5;
    },
    steps() {
      const decision = this.isFinal
        ? {
            key: 'decision',
            label: this.$t(`quotations.statuses.${this.quotation.status}`),
            color: { ORDERED: 'success', NOT_ORDERED: 'error', DECLINED: 'grey', ORDERED_ELSEWHERE: 'teal' }[this.quotation.status],
            icon: { ORDERED: 'mdi-check', NOT_ORDERED: 'mdi-close', DECLINED: 'mdi-cancel', ORDERED_ELSEWHERE: 'mdi-account-switch' }[this.quotation.status],
          }
        : { key: 'decision', label: this.$t('quotations.steps.decision'), color: 'primary', icon: 'mdi-check' };
      return [
        { key: 'REQUEST', label: this.$t('quotations.statuses.REQUEST'), color: 'primary', icon: 'mdi-check' },
        { key: 'CALCULATION', label: this.$t('quotations.statuses.CALCULATION'), color: 'primary', icon: 'mdi-check' },
        { key: 'REVIEW', label: this.$t('quotations.statuses.REVIEW'), color: 'primary', icon: 'mdi-check' },
        { key: 'READY', label: this.$t('quotations.statuses.READY'), color: 'primary', icon: 'mdi-check' },
        decision,
      ];
    },
    actions() {
      const t = k => this.$t(`quotations.actions.${k}`);
      switch (this.quotation.status) {
        case 'REQUEST':
          return [
            { status: 'CALCULATION', label: t('to_calculation') },
            { status: 'DECLINED', label: t('decline') },
          ];
        case 'CALCULATION':
          return [
            { status: 'REVIEW', label: t('to_review'), disabled: this.blockers.length > 0 },
            { status: 'READY', label: t('approve_direct'), disabled: this.blockers.length > 0 },
            { status: 'DECLINED', label: t('decline') },
          ];
        case 'REVIEW':
          return [
            { status: 'READY', label: t('approve_send') },
            { status: 'CALCULATION', label: t('back_to_calc') },
            { status: 'DECLINED', label: t('decline') },
          ];
        case 'READY':
          return [
            { status: 'ORDERED', label: t('accept') },
            { status: 'CALCULATION', label: t('back_to_calc') },
            { status: 'NOT_ORDERED', label: t('not_ordered') },
            { status: 'DECLINED', label: t('decline') },
          ];
        default:
          return [];
      }
    },
    // Lähetys vaiheen rinnalla; lopputilassa ei näytetä
    sentText() {
      const sent = this.quotation.recipient.sentDate;
      return sent && !this.isFinal ? this.$t('quotations.recipients.documents.sent_on', { date: this.$formatDateNoTime(sent) }) : '';
    },
    primary() {
      return this.actions[0] || {};
    },
    secondary() {
      return this.actions.slice(1);
    },
  },
};
</script>

<style lang="scss" scoped>
// Viisi vaihetta: kapeat askeleet, ja jos rivi ei riitä, toiminnot rivittyvät vaihepolun alle
.stepper-plain {
  background: transparent;
  min-width: min(440px, 100%);
  ::v-deep .v-stepper__header {
    box-shadow: none;
    height: auto;
  }
  ::v-deep .v-stepper__step {
    flex-basis: 80px;
    padding: 8px 0;
  }
  ::v-deep .v-stepper__header .v-divider {
    margin: 20px -22px 0;
  }
  ::v-deep .v-stepper__label {
    text-align: center;
    white-space: nowrap;
  }
}
</style>
